import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { archivePosts, archiveYears } from '../../../mocks/archivePosts';
import { categories } from '../../../mocks/blogPosts';

export default function BlogArchive() {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(6);

  const filteredPosts = useMemo(() => {
    let filtered = archivePosts;

    if (selectedYear) {
      filtered = filtered.filter(p => new Date(p.date).getFullYear() === selectedYear);
    }
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.author.name.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [selectedYear, selectedCategory, searchQuery]);

  const postsByYear = useMemo(() => {
    const years: Record<number, typeof archivePosts> = {};
    filteredPosts.forEach(p => {
      const y = new Date(p.date).getFullYear();
      if (!years[y]) years[y] = [];
      years[y].push(p);
    });
    return years;
  }, [filteredPosts]);

  const sortedYears = Object.keys(postsByYear)
    .map(Number)
    .sort((a, b) => b - a);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const categoryColors: Record<string, string> = {
    Cybersecurity: 'bg-red-50 text-red-600 border border-red-100',
    Cloud: 'bg-sky-50 text-sky-600 border border-sky-100',
    'AI & Analytics': 'bg-emerald-50 text-emerald-600 border border-emerald-100',
    'Digital Strategy': 'bg-amber-50 text-amber-600 border border-amber-100',
  };

  const totalCount = archivePosts.length;
  const yearCounts = archiveYears.reduce<Record<number, number>>((acc, y) => {
    acc[y] = archivePosts.filter(p => new Date(p.date).getFullYear() === y).length;
    return acc;
  }, {});

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="flex items-center mb-12">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#0066CC]/10 rounded-lg">
              <i className="ri-archive-line text-[#0066CC] text-xl"></i>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Article Archive</h2>
              <p className="text-sm text-gray-500 mt-0.5">{totalCount} articles from previous years</p>
            </div>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent ml-6"></div>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Sidebar Filters */}
          <aside className="lg:w-64 flex-shrink-0 space-y-6">
            {/* Search */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Search Archive</h3>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-search-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0066CC]/20 focus:border-[#0066CC] outline-none transition-colors text-sm bg-white"
                />
              </div>
            </div>

            {/* Year Filter */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Browse by Year</h3>
              <div className="space-y-1.5">
                <button
                  onClick={() => setSelectedYear(null)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedYear === null
                      ? 'bg-[#0066CC] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span>All Years</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${selectedYear === null ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
                    {totalCount}
                  </span>
                </button>
                {archiveYears.map(year => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      selectedYear === year
                        ? 'bg-[#0066CC] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>{year}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${selectedYear === year ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {yearCounts[year] ?? 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Filter by Topic</h3>
              <div className="space-y-1.5">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      selectedCategory === cat
                        ? 'bg-[#0066CC] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Posts List */}
          <div className="flex-1 min-w-0">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gray-100 rounded-full">
                  <i className="ri-file-search-line text-2xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">No archived articles found</h3>
                <p className="text-gray-500 text-sm">Try adjusting your filters or search query.</p>
              </div>
            ) : (
              <div className="space-y-12">
                {sortedYears.map(year => (
                  <div key={year}>
                    {/* Year Divider */}
                    <div className="flex items-center mb-6">
                      <div className="flex items-center space-x-2 bg-[#0B1120] text-white px-4 py-1.5 rounded-full">
                        <i className="ri-calendar-line text-sm"></i>
                        <span className="text-sm font-bold whitespace-nowrap">{year}</span>
                      </div>
                      <div className="flex-1 h-px bg-gray-200 ml-4"></div>
                      <span className="ml-4 text-xs text-gray-400 whitespace-nowrap">{postsByYear[year].length} articles</span>
                    </div>

                    {/* Posts for this year */}
                    <div className="space-y-4">
                      {postsByYear[year].map(post => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.slug}`}
                          className="group flex gap-5 bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:border-gray-200 transition-all duration-200 cursor-pointer"
                        >
                          {/* Thumbnail */}
                          <div className="w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                            <img
                              src={post.coverImage}
                              alt={post.title}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${categoryColors[post.category] ?? 'bg-gray-100 text-gray-600'}`}>
                                {post.category}
                              </span>
                              <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(post.date)}</span>
                            </div>
                            <h4 className="text-sm font-bold text-gray-900 group-hover:text-[#0066CC] transition-colors line-clamp-2 leading-snug mb-1">
                              {post.title}
                            </h4>
                            <p className="text-xs text-gray-500 line-clamp-1 leading-relaxed">{post.excerpt}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <div className="flex items-center gap-1.5">
                                <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                                  <img src={post.author.avatar} alt={post.author.name} className="w-full h-full object-cover object-top" />
                                </div>
                                <span className="text-xs text-gray-500 whitespace-nowrap">{post.author.name}</span>
                              </div>
                              <span className="text-gray-300">·</span>
                              <div className="flex items-center gap-1 text-xs text-gray-400">
                                <i className="ri-time-line"></i>
                                <span className="whitespace-nowrap">{post.readTime}</span>
                              </div>
                            </div>
                          </div>

                          {/* Arrow */}
                          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-300 group-hover:text-[#0066CC] transition-colors self-center">
                            <i className="ri-arrow-right-line text-lg"></i>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
